<script setup lang="ts">
import AppContextPanel from '@/components/app-shell/AppContextPanel.vue'
import DocumentsSection from '@/components/documents/DocumentsSection.vue'
import FinestAssistantPanel from '@/components/FinestAssistantPanel.vue'
import FinestButton from '@/components/FinestButton.vue'
import SectionTabs, { SectionTab } from '@/components/SectionTabs.vue'
import CompanyMasterData from '@/pages/investors/companies/CompanyMasterData.vue'
import CompanyMembers from '@/pages/investors/companies/CompanyMembers.vue'
import { useFieldStates, type FieldStateMap } from '@/utils/field-state'
import CompaniesController from '@controllers/Investors/CompaniesController'
import { Head } from '@inertiajs/vue3'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import CompanyData = App.Data.Company.CompanyData
import DocumentSectionData = App.Data.Documents.DocumentSectionData
import DocumentTypeOptionData = App.Data.Documents.DocumentTypeOptionData
import NaturalPersonData = App.Data.User.NaturalPersonData

const props = defineProps<{
  company: CompanyData
  availableNaturalPersons: NaturalPersonData[]
  availableCompanies: CompanyData[]
  existingShareholdings: string[]
  companyCompletion: number
  fieldStates: FieldStateMap
  documents: DocumentSectionData
  documentTypeOptions: DocumentTypeOptionData[]
}>()

const { t } = useI18n()

type CompanyTab = 'masterData' | 'members' | 'documents'

const activeTab = ref<CompanyTab>('masterData')

/** Which tab a company field is edited in — layout, so it lives here and not in the catalog. */
const masterDataKeys = [
  'name',
  'address',
  'managing_director',
  'commercial_registration_number',
  'registration_court',
  'date_of_establishment',
  'purpose',
  'email',
  'telephone',
]

const { missingCountIn } = useFieldStates(() => props.fieldStates)

const tabs = computed<SectionTab<CompanyTab>[]>(() => [
  { label: t('tabs.masterData'), value: 'masterData', count: missingCountIn(masterDataKeys) },
  { label: t('tabs.members'), value: 'members', count: missingCountIn(['shareholder']) },
  { label: t('tabs.documents'), value: 'documents', icon: 'pi pi-file', trailing: true },
])
</script>

<template>
  <Head :title="company.name" />

  <AppContextPanel>
    <FinestAssistantPanel :completion="companyCompletion" context="company" :show-documents="false" />
  </AppContextPanel>

  <div class="mb-4">
    <Link :href="CompaniesController.index()">
      <FinestButton :label="t('back')" icon="pi pi-arrow-left" variant="ghost" size="sm" />
    </Link>
  </div>

  <div class="mb-6">
    <p class="section-label">{{ t('eyebrow') }}</p>
    <h1 class="page-title">{{ company.name }}</h1>
    <p v-if="company.address" class="text-ink-2 mt-2 max-w-prose">{{ company.address }}</p>
  </div>

  <SectionTabs v-model="activeTab" :tabs="tabs" :aria-label="company.name || t('eyebrow')" />

  <CompanyMasterData v-if="activeTab === 'masterData'" :company="company" :field-states="fieldStates" />

  <CompanyMembers
    v-else-if="activeTab === 'members'"
    :company="company"
    :available-natural-persons="availableNaturalPersons"
    :available-companies="availableCompanies"
    :existing-shareholdings="existingShareholdings" />

  <DocumentsSection v-else :section="documents" :type-options="documentTypeOptions" />
</template>

<i18n>
{
  "de": {
    "eyebrow": "Meine Basis · Unternehmen",
    "back": "Zur Übersicht",
    "tabs": {
      "masterData": "Stammdaten & Kontakt",
      "members": "Gesellschafter",
      "documents": "Dokumente"
    }
  },
  "en": {
    "eyebrow": "My Base · Companies",
    "back": "Back to overview",
    "tabs": {
      "masterData": "Master data & contact",
      "members": "Shareholders",
      "documents": "Documents"
    }
  }
}
</i18n>
